import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/admin/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/admin/ui/table';
import { Input } from '@/components/admin/ui/input';
import { Badge } from '@/components/admin/ui/badge';
import { Search, Star, Trophy, Gift, CheckCircle, MessageSquare } from 'lucide-react';
import { supabaseAdmin } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { consoleLink } from '@/lib/staging/consoleLink';

interface Review {
  id: string;
  booking_id: string;
  reviewer_id: string;
  photographer_id: string;
  rating: number;
  comment: string;
  is_featured: boolean;
  is_verified: boolean;
  helpful_count: number;
  response: string | null;
  response_at: string | null;
  created_at: string;
  from_rush_delivery_offer: boolean;
  rush_delivery_granted: boolean;
  rush_delivery_granted_at: string | null;
  reviewer?: {
    full_name: string;
    email: string;
  };
  photographer?: {
    user: {
      full_name: string;
    };
  };
}

const Reviews = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedReviewId, setExpandedReviewId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const { data, error } = await supabaseAdmin
        .from('reviews')
        .select(`
          *,
          reviewer:users!reviews_reviewer_id_fkey(full_name, email),
          photographer:photographers!reviews_photographer_id_fkey(
            user:users!photographers_user_id_fkey(full_name)
          )
        `)
        .not('reviewer_id', 'is', null)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Filter to only include reviews with valid reviewer data
      const validReviews = (data || []).filter(review => review.reviewer?.full_name);
      console.log('Fetched reviews:', data?.length, 'Valid reviews:', validReviews.length);
      setReviews(validReviews);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast({
        title: 'Error',
        description: 'Failed to load reviews',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredReviews = useMemo(() => {
    if (!searchQuery.trim()) return reviews;

    const searchLower = searchQuery.toLowerCase();
    return reviews.filter(review =>
      review.reviewer?.full_name?.toLowerCase().includes(searchLower) ||
      review.reviewer?.email?.toLowerCase().includes(searchLower) ||
      review.photographer?.user?.full_name?.toLowerCase().includes(searchLower) ||
      review.comment?.toLowerCase().includes(searchLower)
    );
  }, [reviews, searchQuery]);

  const renderStars = (rating: number) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  const getRatingColor = (rating: number) => {
    if (rating >= 5) return 'bg-green-500/10 text-green-700 border-green-200';
    if (rating >= 4) return 'bg-blue-500/10 text-blue-700 border-blue-200';
    if (rating >= 3) return 'bg-yellow-500/10 text-yellow-700 border-yellow-200';
    return 'bg-red-500/10 text-red-700 border-red-200';
  };

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-foreground">Reviews</h1>
        <p className="text-sm text-muted-foreground mt-1">
          View and manage all customer reviews from completed bookings
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Reviews</CardTitle>
          <CardDescription>Customer feedback from completed bookings</CardDescription>
          <div className="relative mt-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search reviews by customer, photographer, or comment..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading reviews...</div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead>Photographer</TableHead>
                    <TableHead>Rating</TableHead>
                    <TableHead>Comment</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredReviews.map((review) => (
                    <TableRow
                      key={review.id}
                      className="hover:bg-muted/50 transition-colors"
                    >
                      <TableCell className="font-medium">
                        <div className="flex flex-col">
                          <button
                            onClick={() => navigate(consoleLink(`/lp-console/clients/${review.reviewer_id}`))}
                            className="text-left text-primary hover:underline font-medium"
                          >
                            {review.reviewer?.full_name || 'Unknown'}
                          </button>
                          <span className="text-xs text-muted-foreground">{review.reviewer?.email}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => navigate(consoleLink(`/lp-console/talent/${review.photographer_id}`))}
                          className="text-left text-primary hover:underline"
                        >
                          {review.photographer?.user?.full_name || 'Unknown'}
                        </button>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {renderStars(review.rating)}
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${getRatingColor(review.rating)}`}>
                            {review.rating}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-md">
                        <button
                          type="button"
                          onClick={() => setExpandedReviewId(expandedReviewId === review.id ? null : review.id)}
                          className="block w-full text-left text-sm text-foreground hover:text-primary-700"
                        >
                          <span className={expandedReviewId === review.id ? 'whitespace-pre-wrap' : 'line-clamp-2'}>
                            {review.comment || 'No comment'}
                          </span>
                          {review.comment && review.comment.length > 120 && (
                            <span className="mt-1 block text-xs font-medium text-primary-600">
                              {expandedReviewId === review.id ? 'Show less' : 'Read full review'}
                            </span>
                          )}
                        </button>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {format(new Date(review.created_at), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          {review.is_verified && (
                            <Badge variant="outline" className="w-fit bg-blue-50 text-blue-700 border-blue-200">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Verified
                            </Badge>
                          )}
                          {review.response && (
                            <Badge variant="outline" className="w-fit bg-gray-50 text-gray-700 border-gray-200">
                              <MessageSquare className="h-3 w-3 mr-1" />
                              Responded
                            </Badge>
                          )}
                          {review.is_featured && (
                            <Badge variant="secondary" className="w-fit">
                              <Trophy className="h-3 w-3 mr-1" />
                              Featured
                            </Badge>
                          )}
                          {review.from_rush_delivery_offer && (
                            <Badge variant="outline" className="w-fit bg-purple-50 text-purple-700 border-purple-200">
                              <Gift className="h-3 w-3 mr-1" />
                              Expedited Offer
                            </Badge>
                          )}
                          {review.rush_delivery_granted && (
                            <Badge variant="outline" className="w-fit bg-green-50 text-green-700 border-green-200">
                              Expedited Granted
                            </Badge>
                          )}
                          {!review.is_verified && !review.response && !review.is_featured && !review.from_rush_delivery_offer && !review.rush_delivery_granted && (
                            <Badge variant="outline" className="w-fit text-muted-foreground">
                              New
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {filteredReviews.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  {searchQuery ? `No reviews found matching "${searchQuery}"` : 'No reviews found'}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Reviews;
